"use client";

import * as React from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Chat } from "@/components/ui/chat/chat";
import { SearchProviderFromChat } from "@/components/ui/search/search-provider-from-chat";
import { Search } from "@/components/ui/search/search";
import { cn } from "@/lib/utils";
import {
  usePersonation,
  DEFAULT_LAUNCHER_AVATAR_SIZE,
} from "@/lib/personation-context";
import { withAlpha } from "@/lib/color";
import {
  saveWidgetState,
  getWidgetState,
  clearWidgetState,
} from "@/lib/widget-state-storage";
import {
  DEFAULT_WIDTH,
  DEFAULT_HEIGHT,
  FULLSCREEN_DEFAULT_WIDTH,
  FULLSCREEN_DEFAULT_HEIGHT,
} from "@/lib/widget-dimensions-storage";
import { useIsMobile } from "@/lib/useIsMobile";
import { TOP_LAYER_Z } from "@/lib/z-index";
import { navigationEvents, isNavigationPending } from "@/lib/navigation-events";
import {
  WidgetWindow,
  useWidgetWindow,
} from "@/components/ui/widget-window/widget-window";

import {
  WidgetModeProvider,
  useWidgetMode,
  type WidgetMode,
} from "./widget-mode-context";
import { WidgetHeader } from "./widget-header";
import { SourcesBody } from "./sources-body";
import { useWidgetOverlayOptional, OVERLAY_ANIMATION_DURATION_MS, DragHandleProvider, type DragHandleProps } from "./widget-overlay-context";
import { GlobalApiBridge } from "@/components/global-api-bridge";

/**
 * Traverses shadow DOM boundaries to find and blur the deeply active element.
 * `document.activeElement` only returns the shadow host when focus is inside
 * a shadow root, so we must walk into each nested shadow root to reach the
 * actual focused element.
 */
function blurDeepActiveElement(): void {
  let el = document.activeElement as HTMLElement | null;
  while (el?.shadowRoot?.activeElement) {
    el = el.shadowRoot.activeElement as HTMLElement;
  }
  el?.blur();
}

// Triggers focus on current mode's input when widget becomes visible
function WidgetVisibilityFocusHandler({ isVisible }: { isVisible: boolean }) {
  const { triggerFocus } = useWidgetMode();
  const wasVisible = React.useRef(isVisible);

  React.useEffect(() => {
    // Trigger focus when transitioning from hidden to visible
    if (isVisible && !wasVisible.current) {
      triggerFocus();
    }
    wasVisible.current = isVisible;
  }, [isVisible, triggerFocus]);

  return null;
}

/**
 * Dev-only hook that tracks whether an input/textarea is focused inside the widget.
 * Returns true when focused, false otherwise. No-op in production builds.
 */
function useInputFocusDebug() {
  const [isFocused, setIsFocused] = React.useState(false);

  const handleFocusCapture = React.useCallback((e: React.FocusEvent) => {
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") {
      setIsFocused(true);
    }
  }, []);

  const handleBlurCapture = React.useCallback((e: React.FocusEvent) => {
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") {
      setIsFocused(false);
    }
  }, []);

  return { isFocused, handleFocusCapture, handleBlurCapture };
}

// Inner Widget Content (needs access to mode context)
type WidgetContentProps = {
  roomId?: string;
  className?: string;
  isFullScreen: boolean;
  isMobile: boolean;
  onFullScreenToggle: (next: boolean) => void;
  onClose: () => void;
  isDragging?: boolean;
  dragHandleProps?: DragHandleProps;
  initialInput?: string;
  onSearchInputChange?: (input: string) => void;
  onRoomIdChange?: (roomId: string | undefined) => void;
  initialScrollPosition?: number;
  onScrollPositionChange?: (position: number) => void;
  initialSearchScrollPosition?: number;
  onSearchScrollPositionChange?: (position: number) => void;
};

function WidgetContent({
  roomId,
  className,
  isFullScreen,
  isMobile,
  onFullScreenToggle,
  onClose,
  isDragging,
  dragHandleProps,
  initialInput,
  onSearchInputChange,
  onRoomIdChange,
  initialScrollPosition,
  onScrollPositionChange,
  initialSearchScrollPosition,
  onSearchScrollPositionChange,
}: WidgetContentProps) {
  const { mode } = useWidgetMode();
  const { companyMainColor } = usePersonation();

  // Track scroll position to restore when returning from sources
  const scrollPositionRef = React.useRef<number>(initialScrollPosition ?? 0);
  const scrollPositionOnModeSwitchRef = React.useRef<number | null>(null);
  const prevModeRef = React.useRef<WidgetMode>(mode);
  const [scrollPositionToRestore, setScrollPositionToRestore] = React.useState<
    number | undefined
  >(initialScrollPosition);

  // Track scroll position changes
  const handleScrollPositionChangeInternal = React.useCallback((position: number) => {
    scrollPositionRef.current = position;
    onScrollPositionChange?.(position);
  }, [onScrollPositionChange]);

  // Save/restore scroll position on mode changes
  React.useEffect(() => {
    const prevMode = prevModeRef.current;
    const currentMode = mode;

    const isEnteringNonChat =
      prevMode === "chat" &&
      (currentMode === "sources" || currentMode === "search");
    const isReturningToChat =
      currentMode === "chat" &&
      (prevMode === "sources" || prevMode === "search");

    // Entering sources/search from chat - save current scroll position
    if (isEnteringNonChat) {
      scrollPositionOnModeSwitchRef.current = scrollPositionRef.current;
      setScrollPositionToRestore(undefined);
    }

    // Exiting sources/search back to chat - restore saved position
    if (isReturningToChat && scrollPositionOnModeSwitchRef.current !== null) {
      setScrollPositionToRestore(scrollPositionOnModeSwitchRef.current);
      scrollPositionOnModeSwitchRef.current = null;
    }

    prevModeRef.current = currentMode;
  }, [mode]);

  const accentColor = React.useMemo(() => {
    return companyMainColor && companyMainColor.trim()
      ? companyMainColor
      : "#e6001e";
  }, [companyMainColor]);

  return (
    <Chat.Provider
      roomId={roomId}
      isFullScreen={isFullScreen}
      onFullScreenToggle={onFullScreenToggle}
      onCloseRequest={onClose}
      onRoomIdChange={onRoomIdChange}
      initialScrollPosition={scrollPositionToRestore}
      onScrollPositionChange={handleScrollPositionChangeInternal}
      className={className}
    >
      <SearchProviderFromChat
        initialInput={initialInput}
        onInputChange={onSearchInputChange}
        initialScrollPosition={initialSearchScrollPosition}
        onScrollPositionChange={onSearchScrollPositionChange}
      >
        <WidgetContentInner
          mode={mode}
          isFullScreen={isFullScreen}
          isMobile={isMobile}
          accentColor={accentColor}
          onFullScreenToggle={onFullScreenToggle}
          onClose={onClose}
          isDragging={isDragging}
          dragHandleProps={dragHandleProps}
        />
      </SearchProviderFromChat>
    </Chat.Provider>
  );
}


// Inner component that can access ChatContext
type WidgetContentInnerProps = {
  mode: WidgetMode;
  isFullScreen: boolean;
  isMobile: boolean;
  accentColor: string;
  onFullScreenToggle: (next: boolean) => void;
  onClose: () => void;
  isDragging?: boolean;
  dragHandleProps?: DragHandleProps;
};

function WidgetContentInner({
  mode,
  isFullScreen,
  isMobile,
  accentColor,
  onFullScreenToggle,
  onClose,
  isDragging,
  dragHandleProps,
}: WidgetContentInnerProps) {
  const chatContext = React.useContext(Chat.Context);
  const overlay = useWidgetOverlayOptional();

  return (
    <>
      <WidgetHeader
        isFullScreen={isFullScreen}
        isMobile={isMobile}
        accentColor={accentColor}
        onFullScreenToggle={() => onFullScreenToggle(!isFullScreen)}
        onNewRoom={chatContext?.handleNewRoom}
        onClose={onClose}
        isDragging={isDragging}
        dragHandleProps={dragHandleProps}
      />
      <WidgetBody mode={mode} />
      <WidgetFooter mode={mode} />
      {overlay?.overlayContent && (
        <div
          data-testid="widget-inline-overlay"
          className={`absolute inset-0 z-50 bg-background rounded-lg overflow-hidden flex flex-col transition-transform ease-in-out ${
            overlay.isOverlayVisible ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ transitionDuration: `${OVERLAY_ANIMATION_DURATION_MS}ms` }}
        >
          <DragHandleProvider dragHandleProps={dragHandleProps}>
            {overlay.overlayContent}
          </DragHandleProvider>
        </div>
      )}
    </>
  );
}

// WidgetBody - switches between Chat.Body, Search.Body, and Sources
type WidgetBodyProps = {
  mode: WidgetMode;
};

function WidgetBody({ mode }: WidgetBodyProps) {
  return (
    <>
      {mode === "chat" && <Chat.Body />}
      {mode === "search" && <Search.Body />}
      {mode === "sources" && <SourcesBody />}
    </>
  );
}

// WidgetFooter - switches between Chat.Footer and Search.Footer
type WidgetFooterProps = {
  mode: WidgetMode;
};

function WidgetFooter({ mode }: WidgetFooterProps) {
  // Search and sources modes have no footer
  if (mode === "search" || mode === "sources") {
    return null;
  }
  return <Chat.Footer />;
}

type WidgetContentWithWindowStateProps = Omit<
  WidgetContentProps,
  "isFullScreen" | "onFullScreenToggle" | "isDragging" | "dragHandleProps"
>;

function WidgetContentWithWindowState(props: WidgetContentWithWindowStateProps) {
  const { isFullScreen, isDragging, dragHandleProps, toggleFullScreen, close } = useWidgetWindow();

  return (
    <WidgetContent
      {...props}
      isFullScreen={isFullScreen}
      onClose={close}
      isDragging={isDragging}
      dragHandleProps={dragHandleProps}
      onFullScreenToggle={(next) => toggleFullScreen(next)}
    />
  );
}

// Main Widget Component
export type WidgetProps = {
  roomId?: string;
  isLeft?: boolean;
  defaultMode?: WidgetMode;
};

export function Widget(props: WidgetProps) {
  const {
    botAvatarUrl,
    botName,
    launcherAvatarSize,
    companyMainColor,
    forceTopLayer,
    labels,
  } = usePersonation();
  const launcherCallToAction = labels.chat.launcherCallToAction;
  const isMobile = useIsMobile();

  // Read persisted state once on mount
  const persistedState = React.useMemo(() => {
    const state = getWidgetState();
    if (state) {
      clearWidgetState();
    }
    return state;
  }, []);

  const [isVisible, setIsVisible] = React.useState(
    persistedState?.isVisible ?? false
  );
  const [isFullScreen, setIsFullScreen] = React.useState(
    persistedState?.isFullScreen ?? false
  );
  // Chat.Provider (and its network/websocket setup) is expensive to keep
  // alive on every host page even when the widget was never opened. Gates
  // ONLY WidgetContentWithWindowState's own mount — the WidgetWindow.Provider
  // frame around it (Card, DndContext, resize handles) still mounts
  // unconditionally, since it holds no comparable resources. Latch to true
  // on first open and never back: once mounted, keepMountedWhenHidden keeps
  // Chat.Provider alive afterward, so this only needs to gate the FIRST
  // mount, not every visibility toggle.
  const [hasBeenOpened, setHasBeenOpened] = React.useState(
    isVisible || isFullScreen
  );
  // Adjusted during render rather than in an effect (React's documented
  // pattern for deriving state from a prop/state change): avoids the extra
  // commit+effect+re-render an effect-based latch would cost on every open.
  if ((isVisible || isFullScreen) && !hasBeenOpened) {
    setHasBeenOpened(true);
  }

  // Refs to capture current mode, searchInput, roomId, and scrollPosition for beforeunload
  const modeRef = React.useRef<WidgetMode>(persistedState?.mode ?? props.defaultMode ?? "chat");
  const previousModeRef = React.useRef<WidgetMode | null>(null);
  const searchInputRef = React.useRef<string>(persistedState?.searchInput ?? "");
  // Chat.Provider is the single live owner of `roomId` after mount — it
  // stays mounted (via `keepMountedWhenHidden` below) instead of remounting
  // on close, so its own internal state is always the current room. This
  // ref only tracks the latest value for `beforeunload` persistence
  // (`saveWidgetState`); it is never fed back into `roomId` prop below,
  // which stays the one-time initial value from `persistedState`.
  const roomIdRef = React.useRef<string | undefined>(persistedState?.roomId);
  const scrollPositionRef = React.useRef<number | null>(persistedState?.scrollPosition ?? null);
  const searchScrollPositionRef = React.useRef<number | null>(persistedState?.searchScrollPosition ?? null);

  // Callbacks to update refs when mode/input changes
  const handleModeChange = React.useCallback((mode: WidgetMode, previousMode: WidgetMode | null) => {
    modeRef.current = mode;
    previousModeRef.current = previousMode;
  }, []);

  const handleSearchInputChange = React.useCallback((input: string) => {
    searchInputRef.current = input;
  }, []);

  const handleRoomIdChange = React.useCallback((roomId: string | undefined) => {
    roomIdRef.current = roomId;
  }, []);

  const handleScrollPositionChange = React.useCallback((position: number) => {
    scrollPositionRef.current = position;
  }, []);

  const handleSearchScrollPositionChange = React.useCallback((position: number) => {
    searchScrollPositionRef.current = position;
  }, []);

  // Save state on beforeunload
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      const currentMode = modeRef.current;
      const currentPreviousMode = previousModeRef.current;
      // Mode "sources" should never be persisted - convert to previousMode or "chat"
      const modeToSave: "chat" | "search" =
        currentMode === "sources"
          ? (currentPreviousMode === "search" ? "search" : "chat")
          : currentMode;

      // If navigation is pending, save isVisible as false to prevent widget from reopening
      const shouldBeVisible = isNavigationPending() ? false : isVisible;

      saveWidgetState({
        isVisible: shouldBeVisible,
        isFullScreen: isNavigationPending() ? false : isFullScreen,
        mode: modeToSave,
        searchInput: searchInputRef.current,
        roomId: roomIdRef.current,
        scrollPosition: scrollPositionRef.current ?? undefined,
        searchScrollPosition: searchScrollPositionRef.current ?? undefined,
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isVisible, isFullScreen]);

  // Listen for navigation events to close widget before page navigation
  React.useEffect(() => {
    const handleCloseBeforeNavigate = () => {
      setIsVisible(false);
      setIsFullScreen(false);
      blurDeepActiveElement();
    };

    navigationEvents.on("close-before-navigate", handleCloseBeforeNavigate);
    return () => {
      navigationEvents.off("close-before-navigate", handleCloseBeforeNavigate);
    };
  }, []);

  const toggleVisibility = React.useCallback((next: boolean) => {
    setIsVisible(next);
    if (next) {
      if (isMobile) {
        setIsFullScreen(true);
      }
    } else {
      setIsFullScreen(false);
      blurDeepActiveElement();
    }
  }, [isMobile]);

  const isBubbleBeforeAvatar = !props.isLeft;
  const sidePosition = props.isLeft ? "left-6" : "right-6";
  const alignment = props.isLeft ? "items-start" : "items-end";
  const showLauncherCta =
    Boolean(launcherCallToAction) && !isVisible && !isFullScreen;

  const resolvedAvatarSize = React.useMemo(() => {
    const size =
      launcherAvatarSize && launcherAvatarSize > 0
        ? launcherAvatarSize
        : DEFAULT_LAUNCHER_AVATAR_SIZE;
    return Math.max(56, size);
  }, [launcherAvatarSize]);

  const buttonDiameter = React.useMemo(
    () => resolvedAvatarSize + 16,
    [resolvedAvatarSize]
  );

  const bubbleStyles = React.useMemo(() => {
    const accent = companyMainColor?.trim() || "#0f172a";
    return {
      backgroundColor: "#fff",
      borderColor: withAlpha(accent, 0.35),
      color: accent,
    } satisfies React.CSSProperties;
  }, [companyMainColor]);

  const { isFocused: isInputFocused, handleFocusCapture, handleBlurCapture } = useInputFocusDebug();
  const forcedLayerZIndex = React.useMemo(
    () =>
      forceTopLayer
        ? {
            overlay: TOP_LAYER_Z.WIDGET_OVERLAY,
            frame: TOP_LAYER_Z.WIDGET_FRAME,
          }
        : null,
    [forceTopLayer]
  );

  return (
    <div
      className="contents"
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
    >
      {!import.meta.env.PROD && isInputFocused && (
        <div
          data-testid="focus-debug-indicator"
          className="fixed top-2 right-2 w-5 h-5 bg-red-500 rounded z-[99999] pointer-events-none"
        />
      )}
    <WidgetModeProvider
      defaultMode={props.defaultMode}
      initialMode={persistedState?.mode}
      isVisible={isVisible}
      onModeChange={handleModeChange}
    >
      <WidgetVisibilityFocusHandler isVisible={isVisible} />
      <GlobalApiBridge toggleVisibility={toggleVisibility} />
      {/* Launcher Button */}
      <div
        className={cn(
          "pointer-events-none fixed bottom-6 z-40 flex flex-col position-relative",
          sidePosition,
          alignment
        )}
      >
        <div
          className={cn(
            "pointer-events-auto flex items-center z-10",
            isBubbleBeforeAvatar ? "flex-row" : "flex-row-reverse"
          )}
        >
          {showLauncherCta && (
            <div
              data-testid="widget-launcher-cta"
              className="relative flex max-w-[280px] flex-col justify-center rounded-3xl px-5 py-3 text-base font-medium leading-relaxed shadow-2xl"
              style={{
                ...bubbleStyles,
                marginRight: 4,
                marginLeft: isBubbleBeforeAvatar ? 12 : 0,
                boxShadow:
                  "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
              }}
            >
              <span
                className={isBubbleBeforeAvatar ? "text-left" : "text-right"}
              >
                {launcherCallToAction}
              </span>
            </div>
          )}
          <button
            type="button"
            data-testid="widget-launcher-button"
            onClick={() => toggleVisibility(true)}
            className="inline-flex items-center justify-center rounded-full border-border bg-background shadow-2xl transition-transform focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary hover:scale-[1.02]"
            style={{
              width: buttonDiameter,
              height: buttonDiameter,
              boxShadow:
                "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Avatar
              style={{ width: resolvedAvatarSize, height: resolvedAvatarSize }}
            >
              <AvatarImage src={botAvatarUrl} alt={botName} />
              <AvatarFallback>{botName?.[0] ?? "B"}</AvatarFallback>
            </Avatar>
          </button>
        </div>
      </div>

      <WidgetWindow.Provider
        storageKeyPrefix="widget"
        defaultWidth={DEFAULT_WIDTH}
        defaultHeight={DEFAULT_HEIGHT}
        fullscreenWidth={FULLSCREEN_DEFAULT_WIDTH}
        fullscreenHeight={FULLSCREEN_DEFAULT_HEIGHT}
        isVisible={isVisible}
        initialFullScreen={persistedState?.isFullScreen}
        onClose={() => toggleVisibility(false)}
        onFullScreenChange={setIsFullScreen}
        dndId="widget-frame"
        frameTestId="widget-frame"
        isLeft={props.isLeft}
        isMobile={isMobile}
        onClickOutside={() => toggleVisibility(false)}
        keepMountedWhenHidden
        className="shadow-2xl"
        overlayZIndex={forcedLayerZIndex?.overlay}
        frameZIndex={forcedLayerZIndex?.frame}
      >
        {hasBeenOpened && (
          <WidgetContentWithWindowState
            roomId={persistedState?.roomId ?? props.roomId}
            className="w-full h-full"
            isMobile={isMobile}
            onClose={() => toggleVisibility(false)}
            initialInput={persistedState?.searchInput}
            onSearchInputChange={handleSearchInputChange}
            onRoomIdChange={handleRoomIdChange}
            initialScrollPosition={persistedState?.scrollPosition}
            onScrollPositionChange={handleScrollPositionChange}
            initialSearchScrollPosition={persistedState?.searchScrollPosition}
            onSearchScrollPositionChange={handleSearchScrollPositionChange}
          />
        )}
      </WidgetWindow.Provider>
    </WidgetModeProvider>
    </div>
  );
}

Widget.displayName = "Widget";

// Namespaced export
export { WidgetModeProvider, useWidgetMode, WidgetHeader };
